import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, ApiError } from '../api/client';
import type { DashboardResponse } from '../types';

const STATUS_LABELS: Record<string, string> = {
  not_opened: 'Not opened',
  in_progress: 'In progress',
  submitted: 'Submitted',
  updated_after_submission: 'Updated after submission',
};

export default function DashboardPage() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load dashboard.'));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    return statusFilter ? data.rows.filter((r) => r.status === statusFilter) : data.rows;
  }, [data, statusFilter]);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return <p>Loading...</p>;

  return (
    <div>
      <h1>Dashboard</h1>
      <div className="field" style={{ maxWidth: 260 }}>
        <label htmlFor="statusFilter">Filter by status</label>
        <select id="statusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.customerId}>
                <td>
                  <Link to={`/customers/${row.customerId}`}>{row.companyName}</Link>
                </td>
                <td>{row.contactName}</td>
                <td>
                  <span className={`badge status-${row.status}`}>{STATUS_LABELS[row.status]}</span>
                  {row.changedAfterFirstSubmission && <span className="badge changed">changed</span>}
                </td>
                <td>{row.lastActivityAt ? new Date(row.lastActivityAt).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No customers match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
